import React, { useState, useRef, useEffect } from 'react';

interface Option {
  value: string;
  label: string;
  color?: string;
  icon?: React.ReactNode;
}

interface InlineSelectorProps {
  value: string;
  options: Option[];
  onSave: (newValue: string) => void;
  placeholder?: string;
  className?: string;
  displayClassName?: string;
  disabled?: boolean;
  renderDisplay?: (option: Option | undefined) => React.ReactNode;
}

export const InlineSelector: React.FC<InlineSelectorProps> = ({
  value,
  options,
  onSave,
  placeholder = '点击选择...',
  className = '',
  displayClassName = '',
  disabled = false,
  renderDisplay
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(option => option.value === value);

  // 处理选择
  const handleSelect = async (newValue: string) => {
    if (newValue === value) {
      setIsOpen(false);
      return;
    }

    try {
      setIsSaving(true);
      await onSave(newValue);
      setIsOpen(false);
    } catch (error) {
      console.error('保存失败:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // 处理键盘事件
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <div className={`relative ${className} ${isOpen ? 'z-selector-active' : ''}`} ref={containerRef}>
      {/* 显示区域 */}
      <div
        className={`
          cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5 transition-colors
          ${disabled ? 'cursor-not-allowed opacity-50' : ''}
          ${displayClassName}
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        title={disabled ? '' : '点击选择'}
      >
        {renderDisplay ? (
          renderDisplay(selectedOption)
        ) : (
          <span className={selectedOption ? '' : 'text-gray-400 italic'}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        )}
      </div>

      {/* 下拉选项 */}
      {isOpen && (
        <>
          <div className="popup-overlay" />
          <div className="popup-selector">
            {isSaving && (
              <div className="flex items-center justify-center p-3">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                <span className="text-sm text-gray-600">保存中...</span>
              </div>
            )}
            
            {!isSaving && options.map(option => (
              <div
                key={option.value}
                className={`
                  popup-selector-item
                  ${option.value === value ? 'selected' : ''}
                `}
                onClick={() => handleSelect(option.value)}
              >
                {option.icon && (
                  <span className="flex-shrink-0">
                    {option.icon}
                  </span>
                )}
                {option.color && (
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: option.color }}
                  />
                )}
                <span className="flex-1">
                  {option.label}
                </span>
                {option.value === value && (
                  <span className="flex-shrink-0 text-blue-600">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                    </svg>
                  </span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};